import { sqliteTable, text, integer, real, index } from "drizzle-orm/sqlite-core";
import { vehicles } from "./vehicles";
import { rentalLocations } from "./rental-locations";

// ============================================================================
// rental_bookings — การจองรถ
// ============================================================================
export const rentalBookings = sqliteTable("car_rental_rental_bookings", {
  id: text("id").primaryKey(), // ULID
  bookingNo: text("booking_no").notNull(),
  companyId: text("company_id").notNull(),
  branchId: text("branch_id"),
  pickupBranchId: text("pickup_branch_id"),
  returnBranchId: text("return_branch_id"),
  customerId: text("customer_id").notNull(), // Soft FK -> sales_customers.id
  vehicleId: text("vehicle_id").notNull().references(() => vehicles.id), // FK → vehicles
  pickupDatetime: text("pickup_datetime").notNull(),
  returnDatetime: text("return_datetime").notNull(),
  actualReturnDatetime: text("actual_return_datetime"),
  pickupLocation: text("pickup_location"),
  returnLocation: text("return_location"),
  pickupLocationId: text("pickup_location_id").references(() => rentalLocations.id),
  returnLocationId: text("return_location_id").references(() => rentalLocations.id),
  pickupLocationOther: text("pickup_location_other"),
  returnLocationOther: text("return_location_other"),

  // Pricing
  calculationMethod: text("calculation_method", {
    enum: ["24_hour_cycle", "daily"],
  }).notNull().default("daily"),
  rentalDays: integer("rental_days").notNull().default(1),
  dailyRate: real("daily_rate").notNull().default(0),
  rentalAmount: real("rental_amount").notNull().default(0),
  extraHours: integer("extra_hours").notNull().default(0),
  extraHoursCharge: real("extra_hours_charge").notNull().default(0),

  // Driver
  withDriver: integer("with_driver").notNull().default(0),
  driverRatePerDay: real("driver_rate_per_day").default(0),
  totalDriverCost: real("total_driver_cost").default(0),

  // Additional charges & discount
  additionalCharges: real("additional_charges").default(0),
  additionalChargesJson: text("additional_charges_json"), // JSON: [{"description":"ค่าส่งรถ","amount":300}]
  discountAmount: real("discount_amount").default(0),
  discountReason: text("discount_reason"),

  // Late fee
  lateHours: integer("late_hours").default(0),
  lateFeePerHour: real("late_fee_per_hour").default(0),
  totalLateFee: real("total_late_fee").default(0),

  // Tax
  taxTemplateId: text("tax_template_id"), // FK → tax_templates (source of truth for selection)
  taxType: text("tax_type", {
    enum: ["no_tax", "tax_inclusive", "tax_exclusive"],
  }).notNull().default("no_tax"),
  taxRate: real("tax_rate").default(0),
  taxAmount: real("tax_amount").default(0),

  // Security deposit & deposit
  securityDeposit: real("security_deposit").default(0),
  depositAmount: real("deposit_amount").default(0),

  // Totals
  subtotal: real("subtotal").notNull().default(0),
  grandTotal: real("grand_total").notNull().default(0),
  totalPaid: real("total_paid").notNull().default(0),
  outstandingAmount: real("outstanding_amount").notNull().default(0),

  // Penalty & refund
  totalPenaltyAmount: real("total_penalty_amount").default(0),
  refundAmount: real("refund_amount").default(0),

  // Document lifecycle (Frappe-style)
  docStatus: integer("doc_status").notNull().default(0), // 0=Draft, 1=Submitted, 2=Cancelled

  // Workflow (central workflow engine)
  workflowId: text("workflow_id"),              // FK → workflows.id
  currentStateKey: text("current_state_key"),   // e.g. "booked", "confirmed"

  // Legacy: kept for queries/filters that reference workflowState directly
  workflowState: text("workflow_state").notNull().default("booked"),

  // Linked documents (accounting module)
  invoiceId: text("invoice_id"),
  depositPaymentId: text("deposit_payment_id"),
  balancePaymentId: text("balance_payment_id"),
  refundPaymentId: text("refund_payment_id"),

  // Linked documents (rental module)
  pickupId: text("pickup_id"),
  returnId: text("return_id"),

  remarks: text("remarks"),
  createdBy: text("created_by"),
  createdAt: text("created_at").notNull(),
  updatedAt: text("updated_at").notNull(),
  updatedBy: text("updated_by"),
  deletedAt: text("deleted_at"),
  deletedBy: text("deleted_by"),
}, (table) => ({
  companyStateIdx: index("rb_company_state_idx").on(table.companyId, table.workflowState, table.createdAt),
  companyCustomerIdx: index("rb_company_customer_idx").on(table.companyId, table.customerId),
  companyVehicleIdx: index("rb_company_vehicle_idx").on(table.companyId, table.vehicleId),
  companyPickupIdx: index("rb_company_pickup_idx").on(table.companyId, table.pickupDatetime),
  companyPickupBranchIdx: index("rb_company_pickup_branch_idx").on(table.companyId, table.pickupBranchId),
  companyReturnBranchIdx: index("rb_company_return_branch_idx").on(table.companyId, table.returnBranchId),
}));

export type RentalBooking = typeof rentalBookings.$inferSelect;
export type NewRentalBooking = typeof rentalBookings.$inferInsert;
